import React, { useRef, useEffect, useState } from 'react';
import { cn } from '../../utils';
import type { DesignElement } from '../../types/editor.types';
import { useEditorStore } from '../../store';
import { clickable } from "components/ui/interactive";

interface Props {
  element: DesignElement;
  editable?: boolean;
}

function ButtonRenderer({ element }: Props) {
  const { text, href } = element.content as { text: string; href: string };

  const btnStyle: React.CSSProperties = {
    backgroundColor: element.styles.backgroundColor ?? undefined,
    color: element.styles.color ?? undefined,
    padding: element.styles.padding ?? undefined,
    borderRadius: element.styles.borderRadius ?? undefined,
    fontSize: element.styles.fontSize ?? undefined,
    fontWeight: element.styles.fontWeight ?? undefined,
  };

  return (
    <div className={cn(
      'w-full flex',
      element.styles.textAlign === 'left' ? 'justify-start' :
        element.styles.textAlign === 'right' ? 'justify-end' : 'justify-center',
    )}>
      <a
        href={href || '#'}
        onClick={(e) => e.preventDefault()}
        className={cn(
          'inline-block no-underline cursor-default transition-opacity hover:opacity-90',
          !element.styles.backgroundColor && 'bg-indigo-500',
          !element.styles.color && 'text-white',
          !element.styles.padding && 'px-6 py-3',
          !element.styles.borderRadius && 'rounded-md',
          !element.styles.fontSize && 'text-sm',
          !element.styles.fontWeight && 'font-semibold',
        )}
        style={btnStyle}
      >
        {text || 'Click Here'}
      </a>
    </div>
  );
}

function ImageRenderer({ element }: Props) {
  const { src, alt, width } = element.content as { src: string; alt: string; width: string };

  return (
    <div className={cn(
      'w-full',
      element.styles.textAlign === 'left' ? 'text-left' :
        element.styles.textAlign === 'right' ? 'text-right' : 'text-center',
    )}>
      {src ? (
        <img
          src={src}
          alt={alt}
          className="inline-block max-w-full"
          style={{ width: width ?? '100%' }}
        />
      ) : (
        <div className="inline-flex items-center justify-center w-full h-28
          bg-slate-100 border-2 border-dashed border-slate-200 rounded-lg"
        >
          <span className="text-xs text-slate-400">No image — set URL in properties</span>
        </div>
      )}
    </div>
  );
}

function HeadingRenderer({ element, editable }: Props) {
  const { updateElement } = useEditorStore();
  const { text, level } = element.content as { text: string; level: string };
  const Tag = (level ?? 'h2') as React.ElementType;
  const ref = useRef<HTMLElement>(null);

  const handleBlur = () => {
    if (!editable || !ref.current) return;
    const newText = ref.current.innerText;
    if (newText !== text) updateElement(element.id, { content: { ...element.content, text: newText } });
  };

  const sizeClass =
    level === 'h1' ? 'text-3xl font-extrabold' :
      level === 'h2' ? 'text-2xl font-bold' :
        level === 'h3' ? 'text-xl font-bold' :
          level === 'h4' ? 'text-lg font-semibold' : 'text-2xl font-bold';

  // Default: dark text on white card. User override wins.
  const headingStyle: React.CSSProperties = {
    color: element.styles.color ?? '#111827',
    fontSize: element.styles.fontSize ?? undefined,
    fontWeight: element.styles.fontWeight ?? undefined,
    textAlign: element.styles.textAlign ?? undefined,
    lineHeight: element.styles.lineHeight ?? undefined,
  };

  return (
    <Tag
      ref={ref}
      style={headingStyle}
      className={cn('p-2 outline-none leading-snug text-content-secondary dark:text-gray-400', !element.styles.fontSize && sizeClass)}
      contentEditable={editable}
      suppressContentEditableWarning
      onBlur={handleBlur}
      onMouseDown={(e: React.MouseEvent) => editable && e.stopPropagation()}
      onClick={(e: React.MouseEvent) => editable && e.stopPropagation()}
    >
      {text || 'Heading'}
    </Tag>
  );
}

function TextRenderer({ element, editable }: Props) {
  const { updateElement } = useEditorStore();
  const { html } = element.content as { html: string };
  const ref = useRef<HTMLDivElement>(null);

  const handleBlur = () => {
    if (!editable || !ref.current) return;
    const newHtml = ref.current.innerHTML;
    if (newHtml !== html) updateElement(element.id, { content: { ...element.content, html: newHtml } });
  };

  // Styles always applied to both editable and read-only renders
  const textStyle: React.CSSProperties = {
    color:      element.styles.color      ?? '#374151',
    fontSize:   element.styles.fontSize   ?? undefined,
    fontWeight: element.styles.fontWeight ?? undefined,
    lineHeight: element.styles.lineHeight ?? undefined,
    textAlign:  (element.styles.textAlign as React.CSSProperties['textAlign']) ?? undefined,
  };

  if (editable) {
    return (
      <div
        ref={ref}
        style={textStyle}
        className='p-2 outline-none text-sm leading-relaxed prose prose-sm max-w-none text-content-secondary dark:text-gray-400'
        contentEditable
        suppressContentEditableWarning
        onBlur={handleBlur}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        dangerouslySetInnerHTML={{ __html: html || '<p>Text block</p>' }}
      />
    );
  }
  return (
    <div
      className='p-2 outline-none text-sm leading-relaxed prose prose-sm max-w-none'
      style={textStyle}
      dangerouslySetInnerHTML={{ __html: html || '<p>Text block</p>' }}
    />
  );
}

function ParagraphRenderer({ element, editable }: Props) {
  const { updateElement } = useEditorStore();
  const { text } = element.content as { text: string };
  const ref = useRef<HTMLParagraphElement>(null);

  const handleBlur = () => {
    if (!editable || !ref.current) return;
    const newText = ref.current.innerText;
    if (newText !== text) updateElement(element.id, { content: { ...element.content, text: newText } });
  };

  const paraStyle: React.CSSProperties = {
    color: element.styles.color ?? undefined,
    fontSize: element.styles.fontSize ?? undefined,
    fontWeight: element.styles.fontWeight ?? undefined,
    lineHeight: element.styles.lineHeight ?? undefined,
    textAlign: element.styles.textAlign ?? undefined,
  };

  return (
    <p
      ref={ref}
      style={paraStyle}
      className="text-sm text-content-secondary dark:text-gray-400 leading-relaxed p-2 outline-none"
      contentEditable={editable}
      suppressContentEditableWarning
      onBlur={handleBlur}
      onMouseDown={(e: React.MouseEvent) => editable && e.stopPropagation()}
      {...clickable((e: React.MouseEvent) => editable && e.stopPropagation())}
    >
      {text || 'Paragraph'}
    </p>
  );
}

function DividerRenderer({ element }: Props) {
  const hasCustomBorder = element.styles.borderTopWidth || element.styles.borderTopStyle || element.styles.borderTopColor;

  if (hasCustomBorder) {
    return (
      <hr
        className="w-full border-0"
        style={{
          borderTop: `${element.styles.borderTopWidth ?? '1px'} ${element.styles.borderTopStyle ?? 'solid'} ${element.styles.borderTopColor ?? '#e2e8f0'}`,
          margin: element.styles.margin ?? '16px 0',
        }}
      />
    );
  }

  return (
    <hr
      className="w-full my-4 border-0 border-t border-slate-200"
      style={element.styles.margin ? { margin: element.styles.margin } : undefined}
    />
  );
}

function TimerRenderer({ element }: Props) {
  const { endDate, labels } = element.content as {
    endDate: string;
    labels: { days: string; hours: string; minutes: string; seconds: string };
  };
  const lbs = labels ?? { days: 'Days', hours: 'Hours', minutes: 'Minutes', seconds: 'Seconds' };

  const getTimeLeft = () => {
    const diff = Math.max(0, new Date(endDate).getTime() - Date.now());
    return {
      days: String(Math.floor(diff / 86400000)).padStart(2, '0'),
      hours: String(Math.floor((diff % 86400000) / 3600000)).padStart(2, '0'),
      minutes: String(Math.floor((diff % 3600000) / 60000)).padStart(2, '0'),
      seconds: String(Math.floor((diff % 60000) / 1000)).padStart(2, '0'),
    };
  };

  const [time, setTime] = useState(getTimeLeft());
  useEffect(() => {
    const t = setInterval(() => setTime(getTimeLeft()), 1000);
    return () => clearInterval(t);
  }, [endDate]);

  return (
    <div className="w-full overflow-hidden text-center py-3">
      <div className="flex flex-wrap justify-center gap-3">
        {(['days', 'hours', 'minutes', 'seconds'] as const).map((unit) => (
          <div key={unit} className="flex flex-col items-center gap-1">
            {/* Dark digit box — always dark regardless of editor theme */}
            <div className="min-w-[44px] h-11 px-2 rounded-xl
              bg-slate-800 text-white
              flex items-center justify-center
              text-base font-bold font-mono shadow-md"
            >
              {time[unit]}
            </div>
            <span className="text-2xs font-medium text-slate-400 whitespace-nowrap uppercase tracking-wide">
              {lbs[unit]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const SOCIAL_COLORS: Record<string, string> = {
  facebook: '#1877f2',
  twitter: '#1da1f2',
  instagram: '#e1306c',
  linkedin: '#0a66c2',
  youtube: '#ff0000',
  tiktok: '#010101',
  github: '#24292e',
  pinterest: '#e60023',
};

function SocialRenderer({ element }: Props) {
  const { platforms, iconSize } = element.content as {
    platforms: { name: string; url: string }[];
    iconSize: number;
  };
  const size = iconSize ?? 36;

  return (
    <div className={cn(
      'w-full flex flex-wrap gap-2 py-3',
      element.styles.textAlign === 'left' ? 'justify-start' :
        element.styles.textAlign === 'right' ? 'justify-end' : 'justify-center',
    )}>
      {(platforms ?? []).map((p) => (
        <div
          key={p.name}
          title={p.name}
          className="flex items-center justify-center rounded-full
            text-white text-xs font-bold uppercase select-none
            shadow-sm transition-transform hover:scale-110 cursor-default"
          style={{
            width: size,
            height: size,
            backgroundColor: SOCIAL_COLORS[p.name] ?? '#64748b',
            flexShrink: 0,
          }}
        >
          {p.name[0]}
        </div>
      ))}
    </div>
  );
}

/* ─── MENU ───────────────────────────────────────────────── */
function MenuRenderer({ element }: Props) {
  const { items, layout } = element.content as { items: { label: string; href: string }[]; layout: string };

  const linkStyle: React.CSSProperties = {
    fontSize: element.styles.fontSize ?? undefined,
    color: element.styles.color ?? '#475569',
  };

  return (
    <nav className={cn(
      'w-full py-2',
      element.styles.textAlign === 'left' ? 'text-left' :
        element.styles.textAlign === 'right' ? 'text-right' : 'text-center',
    )}>
      <div className={cn(
        'inline-flex flex-wrap gap-4',
        layout === 'vertical' && 'flex-col gap-2',
      )}>
        {(items ?? []).map((item, i) => (
          <a
            key={i}
            href={item.href}
            onClick={(e) => e.preventDefault()}
            className="no-underline font-medium text-sm transition-opacity hover:opacity-70 cursor-default"
            style={linkStyle}
          >
            {item.label}
          </a>
        ))}
      </div>
    </nav>
  );
}

function VideoRenderer({ element }: Props) {
  const { src, thumbnail } = element.content as { src: string; thumbnail: string };

  return (
    <div className={cn(
      'w-full py-2',
      element.styles.textAlign === 'left' ? 'text-left' :
        element.styles.textAlign === 'right' ? 'text-right' : 'text-center',
    )}>
      {thumbnail ? (
        <div className="relative inline-block">
          <img
            src={thumbnail}
            alt="Video thumbnail"
            className="rounded-xl max-w-full max-h-48 object-cover"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="size-14 rounded-full bg-black/60 flex items-center justify-center shadow-lg
              ring-2 ring-white/20 backdrop-blur-sm">
              <span className="text-white text-xl">▶</span>
            </div>
          </div>
        </div>
      ) : (
        /* Placeholder is always dark (like a video screen) regardless of editor theme */
        <div className="w-full h-40 inline-flex flex-col items-center justify-center gap-2
          bg-slate-800 rounded-xl border border-slate-700">
          <span className="text-2xl">▶</span>
          <span className="text-slate-400 text-xs">
            {src ? 'Video preview not available' : 'Add video URL in properties'}
          </span>
        </div>
      )}
    </div>
  );
}

/* ─── TABLE ──────────────────────────────────────────────── */
function TableRenderer({ element }: Props) {
  const { headers, rows } = element.content as { headers: string[]; rows: string[][] };

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-slate-50">
            {(headers ?? []).map((h, i) => (
              <th
                key={i}
                className="px-3 py-2 text-left font-semibold whitespace-nowrap
                  text-slate-900 border-b-2 border-slate-200"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(rows ?? []).map((row, i) => (
            <tr
              key={i}
              className={i % 2 === 0 ? 'bg-surface' : 'bg-slate-50/70'}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  className="px-3 py-2 whitespace-nowrap text-slate-600 border-b border-slate-100"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── HTML ───────────────────────────────────────────────── */
function HtmlRenderer({ element }: Props) {
  const { code } = element.content as { code: string };
  return (
    <div
      className="w-full text-sm text-slate-700"
      dangerouslySetInnerHTML={{ __html: code ?? '' }}
    />
  );
}

/* ─── FORM ───────────────────────────────────────────────── */
function FormRenderer({ element }: Props) {
  type Field = { id: string; type: string; label: string; placeholder: string; required?: boolean };
  const { fields, submitText } = element.content as {
    fields: Field[];
    submitText: string;
    action: string;
  };

  return (
    <div className="w-full p-2">
      <form onSubmit={(e) => e.preventDefault()} className="space-y-3">
        {(fields ?? []).map((field) => (
          <div key={field.id} className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-600">
              {field.label}
              {field.required && <span className="text-red-500 dark:text-red-400 ml-0.5">*</span>}
            </label>
            <input
              type={field.type}
              placeholder={field.placeholder}
              disabled
              className="w-full px-3 py-2 text-sm rounded-lg
                border border-slate-200 bg-surface
                text-slate-700 placeholder:text-slate-400
                focus:outline-none cursor-default"
            />
          </div>
        ))}
        <button
          type="submit"
          className="w-full py-2.5 px-4 rounded-lg text-sm font-semibold
            text-white transition-opacity cursor-default hover:opacity-90"
          style={{ backgroundColor: element.styles.backgroundColor ?? '#6366f1' }}
          onClick={(e) => e.preventDefault()}
        >
          {submitText || 'Submit'}
        </button>
      </form>
    </div>
  );
}

/* ─── COLUMNS ────────────────────────────────────────────── */
function ColumnsRenderer({ element }: Props) {
  const { columnCount, gap } = element.content as { columnCount: number; gap: string };
  const count = columnCount ?? 2;

  return (
    <div
      className="w-full grid p-2"
      style={{
        gridTemplateColumns: `repeat(${count}, 1fr)`,
        gap: gap ?? '16px',
      }}
    >
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="min-h-16 flex items-center justify-center rounded-lg
            border-2 border-dashed border-slate-200"
        >
          <span className="text-xs font-medium text-slate-300">Column {i + 1}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── CAROUSEL ───────────────────────────────────────────── */
function CarouselRenderer({ element }: Props) {
  const { images } = element.content as { images: { src: string; alt: string; href: string }[] };
  const [active, setActive] = useState(0);
  const imgs = images ?? [];

  return (
    <div className="w-full py-2 text-center">
      <div className="relative rounded-xl overflow-hidden h-48 bg-slate-100">
        {imgs.length > 0 && imgs[active].src ? (
          <img
            src={imgs[active].src}
            alt={imgs[active].alt}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-400">
            <span className="text-4xl">🖼</span>
            <span className="text-xs font-medium">Add image URLs in properties</span>
          </div>
        )}

        {imgs.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); setActive((a) => (a - 1 + imgs.length) % imgs.length); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 size-7 rounded-full
                bg-black/50 hover:bg-black/70 text-white
                flex items-center justify-center text-sm transition-colors"
            >‹</button>
            <button
              onClick={(e) => { e.stopPropagation(); setActive((a) => (a + 1) % imgs.length); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 size-7 rounded-full
                bg-black/50 hover:bg-black/70 text-white
                flex items-center justify-center text-sm transition-colors"
            >›</button>
          </>
        )}
      </div>

      {imgs.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2">
          {imgs.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setActive(i); }}
              className={cn(
                'h-1.5 rounded-full transition-all duration-200',
                i === active ? 'w-4 bg-indigo-500' : 'w-1.5 bg-slate-300',
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── REGISTRY ───────────────────────────────────────────── */
const renderers: Record<string, React.ComponentType<Props>> = {
  button: ButtonRenderer,
  image: ImageRenderer,
  heading: HeadingRenderer,
  text: TextRenderer,
  paragraph: ParagraphRenderer,
  divider: DividerRenderer,
  timer: TimerRenderer,
  social: SocialRenderer,
  html: HtmlRenderer,
  menu: MenuRenderer,
  video: VideoRenderer,
  table: TableRenderer,
  form: FormRenderer,
  columns: ColumnsRenderer,
  carousel: CarouselRenderer,
};

export function ElementRenderer({ element, editable = false }: Props) {
  const Renderer = renderers[element.type];
  if (!Renderer) {
    return (
      <div className="p-3 text-xs text-center rounded
        text-slate-400 border border-dashed border-slate-200">
        {element.type} — renderer not yet implemented
      </div>
    );
  }
  return <Renderer element={element} editable={editable} />;
}
